import { type ReactNode, useEffect, useState } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

/** Props to spread onto the drag-handle element (see `DragHandle`). */
export interface DragHandleProps {
  attributes: ReturnType<typeof useSortable>['attributes']
  listeners: ReturnType<typeof useSortable>['listeners']
  /** true while this item is the one currently being dragged. */
  isDragging: boolean
}

interface SortableGridProps<T extends { id: number }> {
  /** Items in their current order. Reordering resyncs when this set changes. */
  items: T[]
  /** Called with the new id order after a drop that actually changed the order. */
  onReorder: (orderedIds: number[]) => void
  /** Classes for the layout container (grid/flex). */
  className?: string
  /** Render one item; spread `handle` onto the drag handle element. */
  renderItem: (item: T, handle: DragHandleProps) => ReactNode
}

function SortableItem<T extends { id: number }>({
  item,
  renderItem,
}: {
  item: T
  renderItem: SortableGridProps<T>['renderItem']
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    // Lift the dragged tile above its neighbours; fade it slightly.
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 1 : undefined,
  }

  return (
    // h-full so the item fills its stretched grid cell — lets a card inside
    // grow to the row height (equal-height rows).
    <div ref={setNodeRef} style={style} className="h-full">
      {renderItem(item, { attributes, listeners, isDragging })}
    </div>
  )
}

/**
 * Generic drag-and-drop reorderable grid (dnd-kit). Keeps only the local *order*
 * (a list of ids) so the tiles move instantly on drop; `onReorder` persists the
 * new id order. The rendered items are always looked up fresh from `items` by id,
 * so content changes (rename, visibility, icon…) show up immediately after a
 * refetch — the local order is untouched because the id set is unchanged. The
 * order resyncs whenever the incoming id set changes (added/removed/refetched
 * order), so there is no flicker.
 *
 * Dragging is started from a handle only — spread the `handle` argument of
 * `renderItem` onto a `DragHandle` so the rest of the card stays clickable.
 */
export function SortableGrid<T extends { id: number }>({
  items,
  onReorder,
  className,
  renderItem,
}: SortableGridProps<T>) {
  const [order, setOrder] = useState<number[]>(() => items.map((i) => i.id))

  // Resync when the underlying set/order changes (added/removed/refetched order).
  const idSignature = items.map((i) => i.id).join(',')
  useEffect(() => {
    setOrder(items.map((i) => i.id))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idSignature])

  // Render fresh items in the local order; drop ids no longer present.
  const byId = new Map(items.map((i) => [i.id, i]))
  const ordered = order
    .map((id) => byId.get(id))
    .filter((i): i is T => i !== undefined)

  const sensors = useSensors(
    // Small distance so a plain click on the handle still isn't read as a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = order.findIndex((id) => id === active.id)
    const newIndex = order.findIndex((id) => id === over.id)
    if (oldIndex < 0 || newIndex < 0) return
    const next = arrayMove(order, oldIndex, newIndex)
    setOrder(next)
    onReorder(next)
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ordered.map((i) => i.id)} strategy={rectSortingStrategy}>
        <div className={className}>
          {ordered.map((item) => (
            <SortableItem key={item.id} item={item} renderItem={renderItem} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  )
}
